import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {MutationObserver | undefined} */
  #accountAvatarObserver;

  /** @type {boolean} */
  #applyingAvatarLabel = false;

  /** @type {number | undefined} */
  #avatarLabelTimer;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#syncAccountDialogPosition();
    window.addEventListener('scroll', this.#syncAccountDialogPosition, { passive: true });
    window.addEventListener('resize', this.#syncAccountDialogPosition);

    const account = this.querySelector('shopify-account');
    account?.addEventListener('open', this.#syncAccountDialogPosition);
    this.#setupSignedInAvatarLabel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    window.removeEventListener('scroll', this.#syncAccountDialogPosition);
    window.removeEventListener('resize', this.#syncAccountDialogPosition);
    this.#accountAvatarObserver?.disconnect();
    this.#accountAvatarObserver = undefined;
    if (this.#avatarLabelTimer) window.clearTimeout(this.#avatarLabelTimer);

    const account = this.querySelector('shopify-account');
    account?.removeEventListener('open', this.#syncAccountDialogPosition);
  }

  /**
   * Replace signed-in circular initials with "Hesabım" pill inside shopify-account shadow DOM.
   * Keeps native open/close behavior (no proxy click).
   */
  #setupSignedInAvatarLabel = async () => {
    const wrapper = this.querySelector('.account-button');
    const account = this.querySelector('shopify-account');
    if (!(wrapper instanceof HTMLElement) || !(account instanceof HTMLElement)) return;

    const label = wrapper.dataset.accountSignedInLabel || 'Hesabım';
    const inEditor = Boolean(window.Shopify?.designMode);

    try {
      await customElements.whenDefined('shopify-account');
    } catch {
      return;
    }

    const apply = () => {
      if (this.#applyingAvatarLabel) return;
      const root = account.shadowRoot;
      if (!root) return;

      this.#applyingAvatarLabel = true;
      this.#accountAvatarObserver?.disconnect();

      try {
        if (!root.getElementById('ph-signed-in-avatar-style')) {
          const style = document.createElement('style');
          style.id = 'ph-signed-in-avatar-style';
          style.textContent = `
            .account-button__avatar {
              border-radius: 6px !important;
              background: #fff !important;
              color: #000 !important;
              padding: 0 8px !important;
              box-shadow: none !important;
              border: 1px solid #a9a9a9 !important;
              width: auto !important;
              height: auto !important;
              min-width: 0 !important;
              min-height: 28px !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
            }

            .account-button__avatar-initials {
              color: #000 !important;
              text-transform: capitalize !important;
              font-size: 13px !important;
              line-height: 1.2 !important;
              white-space: nowrap !important;
            }

            .account-button__avatar img {
              display: none !important;
            }
          `;
          root.appendChild(style);
        }

        const dialog =
          root.querySelector('dialog') ||
          root.querySelector('[part="dialog"]') ||
          root.querySelector('[role="dialog"]');

        /** @param {Element | null | undefined} el */
        const inDialog = (el) => Boolean(el && dialog && dialog.contains(el));

        let initials =
          root.querySelector('.account-button__avatar-initials') ||
          [...root.querySelectorAll('[class*="initial"]')].find((el) => !inDialog(el)) ||
          null;

        let avatar =
          root.querySelector('.account-button__avatar') ||
          (initials instanceof Element ? initials.closest('button, [role="button"]') : null) ||
          [...root.querySelectorAll('button, [role="button"]')].find((el) => !inDialog(el)) ||
          null;

        if (!(avatar instanceof HTMLElement)) return;

        const avatarText = avatar.textContent?.replace(/\s+/g, ' ').trim() || '';
        const looksSignedIn =
          Boolean(initials) ||
          Boolean(avatar.querySelector('img')) ||
          avatarText === label ||
          /^[A-Za-zÀ-ÿİıĞğÜüŞşÖöÇç]{1,2}$/.test(avatarText);

        if (!looksSignedIn) {
          wrapper.classList.remove('account-button--signed-in');
          return;
        }

        // Already customized — skip DOM writes (prevents observer / editor flicker loops)
        if (
          avatar.classList.contains('account-button__avatar') &&
          initials instanceof HTMLElement &&
          initials.classList.contains('account-button__avatar-initials') &&
          initials.textContent === label &&
          wrapper.classList.contains('account-button--signed-in')
        ) {
          return;
        }

        avatar.classList.add('account-button__avatar');

        if (!(initials instanceof HTMLElement)) {
          initials = document.createElement('span');
          initials.className = 'account-button__avatar-initials';
          avatar.replaceChildren(initials);
        } else if (!initials.classList.contains('account-button__avatar-initials')) {
          initials.classList.add('account-button__avatar-initials');
        }

        if (initials.textContent !== label) {
          initials.textContent = label;
        }

        if (avatar.getAttribute('aria-label') !== label) {
          avatar.setAttribute('aria-label', label);
        }
        wrapper.classList.add('account-button--signed-in');
      } finally {
        this.#applyingAvatarLabel = false;
        // Theme editor: one-shot only. Live store: light re-check if Shopify remounts avatar.
        if (!inEditor && account.shadowRoot) {
          this.#accountAvatarObserver?.observe(account.shadowRoot, {
            childList: true,
            subtree: false,
          });
        }
      }
    };

    const scheduleApply = () => {
      if (this.#avatarLabelTimer) window.clearTimeout(this.#avatarLabelTimer);
      this.#avatarLabelTimer = window.setTimeout(() => {
        this.#avatarLabelTimer = undefined;
        apply();
      }, inEditor ? 250 : 80);
    };

    const observeRoot = () => {
      if (!account.shadowRoot) return;
      this.#accountAvatarObserver?.disconnect();
      this.#accountAvatarObserver = new MutationObserver(scheduleApply);
      if (!inEditor) {
        this.#accountAvatarObserver.observe(account.shadowRoot, {
          childList: true,
          subtree: false,
        });
      }
      scheduleApply();
    };

    if (account.shadowRoot) {
      observeRoot();
    } else {
      const hostObserver = new MutationObserver(() => {
        if (!account.shadowRoot) return;
        hostObserver.disconnect();
        observeRoot();
      });
      hostObserver.observe(account, { childList: true, subtree: true, attributes: true });
      window.setTimeout(() => {
        hostObserver.disconnect();
        observeRoot();
      }, 2000);
    }
  };

  /**
   * Anchor the account dialog under the Giriş Yap / Hesabım control,
   * horizontally centered on the button with 1rem top gap.
   */
  #syncAccountDialogPosition = () => {
    const account = this.querySelector('shopify-account');
    const button = this.querySelector('.account-button');
    if (!(account instanceof HTMLElement) || !(button instanceof HTMLElement)) return;

    const rect = button.getBoundingClientRect();
    const top = Math.max(0, Math.round(rect.bottom + 16)); // 1rem gap
    const centerX = rect.left + rect.width / 2;

    account.style.setProperty('--shopify-account-dialog-position-top', `${top}px`);
    account.style.setProperty('--account-dialog-center-x', `${Math.round(centerX)}px`);
  };

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
